/* @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Dialog, Grid, SelectField, SelectOption, TextField, Typography2, VisuallyHidden } from '@coursera/cds-core';

import { CoachButton } from 'bundles/ai-coach-platform/components/building-blocks';

import _t from 'i18n!nls/ai-coach-platform';

export type FeedbackIssueOption = {
  value: string;
  label: string;
};

type Props = {
  title: string;
  placeholder?: string;
  /** When provided, renders an issue-category SelectField above the comments TextField. */
  issueOptions?: FeedbackIssueOption[];
  onSubmit(args: { userComments: string; userIssue?: string }): void;
  onClose(e: React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element>): void;
  onCancel(e: React.MouseEvent<Element, MouseEvent>): void;
};

const FeedbackModal = ({ title, placeholder, issueOptions, onSubmit, onClose, onCancel, ...rest }: Props) => {
  const { HeadingGroup, Content, Actions } = Dialog;
  const [show, setShow] = React.useState(true);
  const textFieldRef = React.useRef<HTMLInputElement>(null);
  const [feedbackText, setFeedbackText] = React.useState('');
  const [selectedIssue, setSelectedIssue] = React.useState('');

  const hasIssueOptions = Boolean(issueOptions && issueOptions.length > 0);
  // When the SelectField is shown both fields are explicitly optional in the design, so
  // allow submission with either populated. Otherwise preserve the original gate.
  const submitDisabled = hasIssueOptions ? !feedbackText && !selectedIssue : !feedbackText;

  const handleOnClose = (e: React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element>) => {
    setShow(false);
    onClose(e);
  };

  const handleOnSubmit = () => {
    setShow(false);
    onSubmit({
      userComments: feedbackText,
      ...(selectedIssue && { userIssue: selectedIssue }),
    });
  };

  const handleOnCancel = (e: React.MouseEvent<Element, MouseEvent>) => {
    setShow(false);
    onCancel(e);
  };

  const shareDetailsLabel = _t('Share details');
  const textFieldLabel = hasIssueOptions ? shareDetailsLabel : title;
  const renderTextFieldLabel = hasIssueOptions
    ? () => <VisuallyHidden>{shareDetailsLabel}</VisuallyHidden>
    : () => (
        <Typography2 variant="titleMedium" component="h1">
          {title}
        </Typography2>
      );

  return (
    <Dialog aria-labelledby="dialog-header" open={show} sticky="actions" onClose={handleOnClose} {...rest}>
      {hasIssueOptions ? (
        <HeadingGroup id="dialog-header">{title}</HeadingGroup>
      ) : (
        <VisuallyHidden id="dialog-header">{_t('Response feedback')}</VisuallyHidden>
      )}

      <Content id="dialog-content">
        {hasIssueOptions && (
          <SelectField
            optional
            fullWidth
            label={_t('What was the issue?')}
            placeholder={_t('Select')}
            value={selectedIssue}
            onChange={(e) => {
              const value = String(e.target.value);
              setSelectedIssue(value);
            }}
            data-testid="feedback-issue-select"
          >
            {issueOptions!.map(({ value, label }) => (
              <SelectOption key={value} value={value}>
                {label}
              </SelectOption>
            ))}
          </SelectField>
        )}
        <Grid container>
          <Grid item sm={12} xs={12}>
            <TextField
              maxLength={255}
              onChange={(e) => setFeedbackText(e.target.value)}
              placeholder={placeholder}
              inputRef={textFieldRef}
              multiline
              fullWidth
              label={textFieldLabel}
              renderLabel={renderTextFieldLabel}
            />
          </Grid>
        </Grid>
      </Content>

      <Actions
        css={css`
          display: flex;
          gap: var(--cds-spacing-200);
        `}
      >
        <CoachButton size="medium" variant="primary" onClick={handleOnSubmit} aria-disabled={submitDisabled}>
          {_t('Submit')}
        </CoachButton>

        <CoachButton size="medium" variant="secondary" onClick={handleOnCancel}>
          {_t('Cancel')}
        </CoachButton>
      </Actions>
    </Dialog>
  );
};

export default FeedbackModal;
